import {
  GuideActor,
  GuideChangeLogEntry,
  GuideMediaAsset,
  GuideTab,
  GuideTopic,
  UserGuideConfig,
  UserGuideTabId,
} from '../types';

export const GUIDE_ANNOUNCEMENTS_TAB_ID: UserGuideTabId = 'announcements';

export const createGuideId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2, 10)}`;

export const getGuideTab = (
  config: UserGuideConfig | undefined,
  tabId: UserGuideTabId,
) => config?.tabs.find(tab => tab.id === tabId);

export const getGuideTopic = (
  tab: GuideTab | undefined,
  topicId: string | null,
) => tab?.topics.find(topic => topic.id === topicId);

type LegacyGuideImage = {
  id?: string;
  src?: string;
  assetKey?: string;
  alt?: string;
  caption?: string;
  title?: string;
};

type LegacyGuideStep = {
  id?: string;
  title?: string;
  description?: string;
  contentHtml?: string;
  images?: LegacyGuideImage[];
  media?: LegacyGuideImage[];
};

type LegacyGuideTopic = Partial<GuideTopic> & {
  contentTitle?: string;
  media?: LegacyGuideImage[];
  steps?: LegacyGuideStep[];
};

const asString = (value: unknown, fallback = '') =>
  typeof value === 'string' ? value : fallback;

const asArray = <T>(value: T[] | undefined): T[] =>
  Array.isArray(value) ? value : [];

const buildCreativeTestingAssetSrcFromKey = (assetKey: string) => {
  const normalizedKey = asString(assetKey).trim();
  if (!normalizedKey) return '';

  const segments = normalizedKey.split('/').filter(Boolean);
  const fileName = segments.pop() || '';
  const assetPath = segments.join('/');

  return buildCreativeTestingAssetSrc(assetPath, fileName);
};

const getLegacyUserGuideAssetKey = (value: string) => {
  const source = asString(value).trim();
  if (!source) return '';

  try {
    const parsed = new URL(source, 'https://placeholder.local');
    const match = parsed.pathname.match(/\/api\/v1\/user-guide\/media\/(.+)$/);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

const rewriteLegacyGuideMediaUrls = (value: string) =>
  asString(value).replace(
    /(?:https?:\/\/[^/"']+)?\/api\/v1\/user-guide\/media\/([^"' )]+)/g,
    (_match, encodedKey) => {
      const assetKey = decodeURIComponent(encodedKey);
      return buildCreativeTestingAssetSrcFromKey(assetKey) || _match;
    },
  );

const normalizeGuideActor = (actor: Partial<GuideActor> | undefined): GuideActor => ({
  id: asString(actor?.id, 'unknown'),
  email: asString(actor?.email),
  name: asString(actor?.name, asString(actor?.email, 'Unknown User')),
});

const normalizeGuideChangeLogEntry = (
  entry: Partial<GuideChangeLogEntry>,
  index: number,
): GuideChangeLogEntry => ({
  id: asString(entry?.id, `log-${index + 1}`),
  type: asString(entry?.type, 'config-save'),
  at: asString(entry?.at, new Date().toISOString()),
  version: Number.isFinite(Number(entry?.version))
    ? Math.max(1, Math.floor(Number(entry?.version)))
    : 1,
  summary: asString(entry?.summary),
  actor: normalizeGuideActor(entry?.actor),
});

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const stripGuideHtml = (value: string): string => {
  const source = asString(value);
  if (!source) return '';

  // Parse-like stripping: drop everything inside angle brackets, including
  // truncated tags without a closing '>' (e.g. "<script").
  let out = '';
  let inTag = false;
  for (let i = 0; i < source.length; i += 1) {
    const ch = source[i];
    if (ch === '<') {
      inTag = true;
      continue;
    }
    if (ch === '>') {
      inTag = false;
      continue;
    }
    if (!inTag) out += ch;
  }

  // Decode only safe entities. Keep &lt;/&gt; encoded so '<script' cannot reappear.
  return out
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

const imageToHtml = (image: LegacyGuideImage) => {
  if (!image.src?.trim()) return '';

  const alt = escapeHtml(asString(image.alt || image.caption || image.title));
  const caption = asString(image.caption || image.title);

  return [
    '<figure>',
    `<img src="${escapeHtml(image.src)}" alt="${alt}" />`,
    caption ? `<figcaption>${escapeHtml(caption)}</figcaption>` : '',
    '</figure>',
  ]
    .filter(Boolean)
    .join('');
};

const stepToHtml = (step: LegacyGuideStep, index: number) => {
  const title = asString(step.title, `Step ${index + 1}`);
  const description = asString(step.description || stripGuideHtml(step.contentHtml || ''));
  const images = asArray(step.images || step.media)
    .map(imageToHtml)
    .filter(Boolean)
    .join('');

  return [
    `<h2>${escapeHtml(title)}</h2>`,
    description ? `<p>${escapeHtml(description)}</p>` : '',
    images,
  ]
    .filter(Boolean)
    .join('');
};

const getLegacyTopicHtml = (topic: LegacyGuideTopic) => {
  if (asString(topic.contentHtml).trim()) return asString(topic.contentHtml);

  const steps = asArray(topic.steps);
  if (steps.length) {
    return steps.map(stepToHtml).join('');
  }

  const title = asString(topic.contentTitle || topic.title);
  const summary = asString(topic.summary);
  const media = asArray(topic.media)
    .map(imageToHtml)
    .filter(Boolean)
    .join('');

  return [
    title ? `<h2>${escapeHtml(title)}</h2>` : '',
    summary ? `<p>${escapeHtml(summary)}</p>` : '',
    media,
  ]
    .filter(Boolean)
    .join('');
};

const getSummaryFromHtml = (value: string) => {
  const summary = stripGuideHtml(value).replace(/\s+/g, ' ').trim();
  return summary.length > 140 ? `${summary.slice(0, 137)}...` : summary;
};

export const getGuideTopicHtml = (topic?: GuideTopic | null) =>
  asString(topic?.contentHtml);

export const normalizeGuideTopic = (
  topic: LegacyGuideTopic,
  index = 0,
): GuideTopic => {
  const now = new Date().toISOString();
  const contentHtml = rewriteLegacyGuideMediaUrls(getLegacyTopicHtml(topic));

  return {
    id: asString(topic?.id, `topic-${index + 1}`),
    title: asString(topic?.title, `Topic ${index + 1}`),
    contentHtml,
    createdAt: asString(topic?.createdAt, asString(topic?.updatedAt, now)),
    updatedAt: asString(topic?.updatedAt, now),
    announcementNote: asString(topic?.announcementNote),
    summary: asString(topic?.summary, getSummaryFromHtml(contentHtml)),
  };
};

const normalizeGuideMediaAsset = (
  asset: Partial<GuideMediaAsset>,
  index: number,
): GuideMediaAsset => {
  const type = asset?.type === 'video' ? 'video' : 'image';

  return {
    id: asString(asset?.id, `media-${index + 1}`),
    name: asString(asset?.name, `Media ${index + 1}`),
    type,
    src:
      buildCreativeTestingAssetSrc(
        asString((asset as any)?.path),
        asString((asset as any)?.fileName),
      ) ||
      buildCreativeTestingAssetSrcFromKey(asString((asset as any)?.assetKey)) ||
      buildCreativeTestingAssetSrcFromKey(getLegacyUserGuideAssetKey(asString(asset?.src))) ||
      asString(asset?.src),
    alt: asString(asset?.alt),
    createdAt: asString(asset?.createdAt, new Date().toISOString()),
    assetKey: asString((asset as any)?.assetKey),
    fileName: asString((asset as any)?.fileName),
    path: asString((asset as any)?.path),
    contentType: asString((asset as any)?.contentType),
    uploadedAt: asString((asset as any)?.uploadedAt || asset?.createdAt),
    uploadedBy: (asset as any)?.uploadedBy
      ? normalizeGuideActor((asset as any).uploadedBy)
      : undefined,
  };
};

export const normalizeGuideConfig = (
  config: UserGuideConfig | undefined,
): UserGuideConfig | undefined => {
  if (!config) return undefined;

  return {
    ...config,
    announcementHighlightDays: Number.isFinite(config.announcementHighlightDays)
      ? config.announcementHighlightDays
      : 15,
    updatedBy: (config as any)?.updatedBy
      ? normalizeGuideActor((config as any).updatedBy)
      : undefined,
    changeLog: Array.isArray((config as any)?.changeLog)
      ? (config as any).changeLog.map(normalizeGuideChangeLogEntry)
      : [],
    tabs: Array.isArray(config.tabs)
      ? config.tabs.map(tab => ({
          ...tab,
          description: asString(tab?.description),
          label: asString(tab?.label, tab?.layout?.title || 'Guide'),
          layout: {
            eyebrow: asString(tab?.layout?.eyebrow, 'Guide'),
            title: asString(tab?.layout?.title, tab?.label || 'Guide'),
            description: asString(tab?.layout?.description),
            leftPanelTitle: asString(tab?.layout?.leftPanelTitle, 'Topics'),
            emptyStateTitle: asString(
              tab?.layout?.emptyStateTitle,
              'Select a topic',
            ),
            emptyStateDescription: asString(
              tab?.layout?.emptyStateDescription,
              'Choose a topic from the left panel.',
            ),
          },
          topics: Array.isArray(tab?.topics)
            ? tab.topics.map((topic, index) =>
                normalizeGuideTopic(topic as LegacyGuideTopic, index),
              )
            : [],
        }))
      : [],
    mediaLibrary: Array.isArray(config.mediaLibrary)
      ? config.mediaLibrary.map(normalizeGuideMediaAsset)
      : [],
  };
};

export const cloneGuideConfig = (config: UserGuideConfig): UserGuideConfig =>
  normalizeGuideConfig(JSON.parse(JSON.stringify(config))) as UserGuideConfig;

export const buildMediaEmbedSnippet = (asset: GuideMediaAsset) => {
  const source = escapeHtml(asset.src || '');
  const alt = escapeHtml(asset.alt || asset.name || '');

  if (asset.type === 'video') {
    return `<video controls src="${source}"></video>`;
  }

  return `<img src="${source}" alt="${alt}" />`;
};

export function buildCreativeTestingAssetSrc(
  assetPath?: string,
  fileName?: string,
) {
  const normalizedPath = asString(assetPath).trim();
  const normalizedFileName = asString(fileName).trim();

  if (!normalizedPath || !normalizedFileName) return '';

  return `/api/creative-testing/v1/asset/${encodeURIComponent(
    normalizedFileName,
  )}?path=${encodeURIComponent(normalizedPath)}`;
}

export const isTopicRecentlyUpdated = (
  topic: GuideTopic,
  highlightDays: number,
) => {
  const topicTimestamp = new Date(topic.createdAt || topic.updatedAt).getTime();
  if (Number.isNaN(topicTimestamp)) return false;

  return Date.now() - topicTimestamp <= highlightDays * 24 * 60 * 60 * 1000;
};

export const hasRecentAnnouncements = (
  config: UserGuideConfig | undefined,
  highlightDays?: number,
) => {
  const announcementTab = getGuideTab(config, GUIDE_ANNOUNCEMENTS_TAB_ID);
  const days = highlightDays || config?.announcementHighlightDays || 0;

  if (!announcementTab || !days) return false;

  return announcementTab.topics.some(topic =>
    isTopicRecentlyUpdated(topic, days),
  );
};
